/**
 * Translations Store - Stores article translations with Legend-State syncedSupabase
 *
 * For shared list articles: translations are shared (article-level, not user-level).
 * For inbox articles: translations are private (per user).
 *
 * The translations field is a JSONB object: { 0: "翻译文本", 1: "..." }
 */

import { observable } from '@legendapp/state';
import { syncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { defaultSyncConfig, createSyncCreate, createSyncUpdate } from './syncHelpers';

// Composite unique key for translations: (article_id, user_id, target_language)
// This is used for upsert conflict resolution when duplicate key errors occur
const translationsOnConflict = 'article_id,user_id,target_language';

export const translations$ = observable(
  syncedSupabase({
    supabase,
    collection: 'reedle_translations',
    select: (from) =>
      from.select('id, article_id, user_id, target_language, translations, created_at, updated_at, deleted'),
    // RLS handles permissions: own translations + shared list article translations
    filter: (select) => select.or('deleted.is.null,deleted.eq.false'),
    ...defaultSyncConfig,
    create: createSyncCreate('reedle_translations', { onConflict: translationsOnConflict }),
    update: createSyncUpdate('reedle_translations', { onConflict: translationsOnConflict }),
    // Enable realtime for shared translations
    realtime: true,
  })
);

/**
 * Get translation for article/language
 * For shared list articles (isShared=true): finds ANY user's translation
 * For inbox articles (isShared=false): finds only current user's translation
 *
 * @param {string} articleId
 * @param {string} targetLanguage
 * @param {boolean} isShared - Whether article is in a shared list
 */
export const getTranslation = (articleId, targetLanguage, isShared = false) => {
  const all = Object.values(translations$.get() || {});
  const currentUserId = auth$.user.get()?.id;

  return all.find(t => {
    if (t.article_id !== articleId) return false;
    if (t.target_language !== targetLanguage) return false;
    if (t.deleted) return false;
    // For shared: any user's translation; for private: only own
    if (!isShared && t.user_id !== currentUserId) return false;
    return true;
  });
};

// Built once per translations change. Values are comma-joined primitives so a
// reader of one article key is only notified when that article's languages
// change, not on every per-block translation write.
const translationLanguageIndex$ = observable(() => {
  const currentUserId = auth$.user.get()?.id;
  const index = { all: {}, own: {} };
  const add = (bucket, t) => {
    const languages = bucket[t.article_id] ? bucket[t.article_id].split(',') : [];
    if (!languages.includes(t.target_language)) bucket[t.article_id] = [...languages, t.target_language].join(',');
  };
  Object.values(translations$.get() || {}).forEach(t => {
    if (!t || t.deleted) return;
    add(index.all, t);
    if (t.user_id === currentUserId) add(index.own, t);
  });
  return index;
});

/**
 * Get ALL existing translation languages for an article
 * For shared list articles: returns all languages from any user
 * For inbox articles: returns only current user's languages
 *
 * @param {string} articleId
 * @param {boolean} isShared - Whether article is in a shared list
 */
export const getArticleTranslationLanguages = (articleId, isShared = false) => {
  const languages = translationLanguageIndex$[isShared ? 'all' : 'own'][articleId].get();
  return languages ? languages.split(',') : [];
};

/**
 * Get any existing translation for article (returns the first found language)
 * @param {string} articleId
 * @param {boolean} isShared
 */
export const getExistingTranslationLanguage = (articleId, isShared = false) => {
  const languages = getArticleTranslationLanguages(articleId, isShared);
  return languages[0] || null;
};

/**
 * Check if an article has any translation
 * @param {string} articleId
 * @param {boolean} isShared
 */
export const getArticleTranslationInfo = (articleId, isShared = false) => {
  const languages = getArticleTranslationLanguages(articleId, isShared);
  return {
    hasTranslation: languages.length > 0,
    languages,
  };
};

/**
 * Get all translated titles for an article
 * Returns array of {language, title} objects for all available translations
 * @param {string} articleId
 * @param {boolean} isShared
 */
export const getArticleTranslatedTitles = (articleId, isShared = false) => {
  const all = Object.values(translations$.get() || {});
  const currentUserId = auth$.user.get()?.id;

  const titles = [];
  all.forEach(t => {
    if (t.article_id !== articleId) return;
    if (t.deleted) return;
    if (!isShared && t.user_id !== currentUserId) return;

    // Check if this translation has a title
    const translationData = t.translations;
    if (translationData && typeof translationData === 'object') {
      const titleText = translationData.title;
      if (titleText && typeof titleText === 'string') {
        titles.push({
          language: t.target_language,
          title: titleText,
        });
      }
    }
  });

  return titles;
};

/**
 * Get set of article IDs that have translations
 * For article list badge - checks both own and shared translations
 */
export const getTranslatedArticleIds = () => {
  const all = Object.values(translations$.get() || {});
  const ids = new Set();
  all.forEach(t => {
    if (!t.deleted) {
      ids.add(t.article_id);
    }
  });
  return ids;
};

/**
 * Get translations for blocks as a Map<blockIndex, translatedText>
 * @param {string} articleId
 * @param {string} targetLanguage
 * @param {boolean} isShared
 */
export const getTranslationMap = (articleId, targetLanguage, isShared = false) => {
  const translation = getTranslation(articleId, targetLanguage, isShared);
  if (!translation?.translations) return new Map();

  const map = new Map();
  const data = translation.translations;

  // New format: object with numeric/string keys {0: "text", 1: "text", "title": "translated title"}
  if (typeof data === 'object' && !Array.isArray(data)) {
    Object.entries(data).forEach(([key, text]) => {
      if (typeof text !== 'string') return;
      // Special "title" key for translated title
      if (key === 'title') {
        map.set('title', text);
        return;
      }
      // Numeric block indices
      const idx = parseInt(key, 10);
      if (!isNaN(idx)) {
        map.set(idx, text);
      }
    });
  }
  // Legacy format: array [{block_index: 0, text: "..."}]
  else if (Array.isArray(data)) {
    data.forEach(t => {
      if (t && typeof t.block_index === 'number') {
        map.set(t.block_index, t.text);
      }
    });
  }

  return map;
};

/**
 * Update single block translation (for progressive translation)
 * For shared articles: updates existing shared record or creates new one
 * For private articles: updates own record or creates new one
 *
 * @param {string} articleId
 * @param {string} targetLanguage
 * @param {number} blockIndex
 * @param {string} text
 * @param {boolean} isShared - Whether article is in a shared list
 * @returns {string} The translation record ID
 */
export const updateBlockTranslation = (articleId, targetLanguage, blockIndex, text, isShared = false) => {
  const existing = getTranslation(articleId, targetLanguage, isShared);
  const currentUserId = auth$.user.get()?.id;
  const now = new Date().toISOString();

  if (existing) {
    // Update single block in existing record (even if created by another user for shared)
    const currentTranslations = existing.translations || {};
    translations$[existing.id].assign({
      translations: {
        ...currentTranslations,
        [blockIndex]: text,
      },
      updated_at: now,
    });
    return existing.id;
  } else {
    // Create new record with single block (owned by current user)
    const id = crypto.randomUUID();
    translations$[id].set({
      id,
      article_id: articleId,
      user_id: currentUserId,
      target_language: targetLanguage,
      translations: { [blockIndex]: text },
      created_at: now,
      updated_at: now,
      deleted: false,
    });
    return id;
  }
};

/**
 * Check if current user owns the translation for this article/language
 * Used to determine if "Clear all" should be allowed
 */
export const isOwnTranslation = (articleId, targetLanguage, isShared = false) => {
  const translation = getTranslation(articleId, targetLanguage, isShared);
  if (!translation) return false;
  const currentUserId = auth$.user.get()?.id;
  return translation.user_id === currentUserId;
};

/**
 * Delete translation (soft delete) - only works if user owns the translation
 */
export const deleteTranslation = (translationId) => {
  if (!translationId) return false;
  const currentUserId = auth$.user.get()?.id;
  const translation = translations$[translationId].get();

  // Only owner can delete
  if (translation?.user_id !== currentUserId) return false;

  const now = new Date().toISOString();
  translations$[translationId].assign({
    deleted: true,
    updated_at: now,
  });
  return true;
};

/**
 * Delete all translations for an article (soft delete)
 * Only deletes translations owned by current user
 */
export const deleteAllTranslationsForArticle = (articleId) => {
  const all = Object.values(translations$.get() || {});
  const currentUserId = auth$.user.get()?.id;
  const now = new Date().toISOString();
  let deletedCount = 0;

  all.forEach(t => {
    // Only delete own translations
    if (t.article_id === articleId && t.user_id === currentUserId && !t.deleted) {
      translations$[t.id].assign({
        deleted: true,
        updated_at: now,
      });
      deletedCount++;
    }
  });

  return deletedCount;
};
